import { useChatChannel } from "@lib/hooks/useChatChannel";
import { useChatStore } from "@lib/stores/chatStore";
import type { ChatAttachment, ChatRichTextArtefact } from "@lib/types";
import { History, MessageSquarePlus, WifiOff, X } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { ChatComposerView } from "./ChatComposerView";
import { ChatThreadView } from "./ChatThread";
import { ThreadList } from "./ThreadList";

export function ChatPanel() {
  const isOpen = useChatStore((s) => s.isOpen);
  const toggle = useChatStore((s) => s.toggle);
  const close = useChatStore((s) => s.close);
  const panelWidth = useChatStore((s) => s.panelWidth);
  const setPanelWidth = useChatStore((s) => s.setPanelWidth);
  const threads = useChatStore((s) => s.threads);
  const currentThreadId = useChatStore((s) => s.currentThreadId);
  const showHistory = useChatStore((s) => s.showHistory);
  const setShowHistory = useChatStore((s) => s.setShowHistory);
  const startNewChat = useChatStore((s) => s.startNewChat);
  const createThread = useChatStore((s) => s.createThread);
  const isSending = useChatStore((s) => s.isSending);
  const sessionId = useChatStore((s) => s.sessionId);
  const fetchThreads = useChatStore((s) => s.fetchThreads);
  const fetchMessages = useChatStore((s) => s.fetchMessages);
  const connectionState = useChatStore((s) => s.connectionState);

  // Subscribe to ActionCable (sessionId is set by fetchThreads in the store)
  useChatChannel(sessionId);

  // On mount, restore persisted state: fetch threads (and messages if a thread was open)
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time mount effect
  useEffect(() => {
    if (isOpen && !sessionId) {
      fetchThreads().then(() => {
        const threadId = currentThreadId;
        if (threadId) fetchMessages(threadId);
      });
    }
  }, []);

  // Keyboard shortcut: Cmd+K to toggle, Escape to close
  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        close();
      }
    },
    [toggle, close, isOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  // Resize drag handling
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: panelWidth };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = dragRef.current.startX - ev.clientX;
        setPanelWidth(dragRef.current.startWidth + delta);
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [panelWidth, setPanelWidth],
  );

  const currentThread = currentThreadId ? threads.find((t) => t.id === currentThreadId) : null;

  // The model is fixed at organisation level — new threads always use it, so
  // there is no picker here and nothing model-related to send.
  function handleNewThreadMessage(
    message: string,
    attachments: ChatAttachment[],
    richTextArtefacts: ChatRichTextArtefact[],
    options: { planModeEnabled: boolean },
  ) {
    createThread(message, attachments, options.planModeEnabled, richTextArtefacts);
  }

  return (
    <div
      className="relative flex flex-col h-full flex-shrink-0 border-l border-border bg-card"
      style={{ width: panelWidth }}
    >
      {/* Resize handle */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: resize drag handle */}
      <div
        onMouseDown={handleResizeMouseDown}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 z-10"
      />
      {/* Panel header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-text-primary">
            {showHistory && !currentThreadId ? "History" : "Chat"}
          </h2>
          {connectionState !== "connected" && (
            <span
              className="flex items-center gap-1 text-xs text-text-tertiary"
              role="status"
              title={
                connectionState === "reconnecting"
                  ? "Connection to live updates lost — reconnecting"
                  : "Live updates were refused by the server"
              }
            >
              <WifiOff size={12} />
              {connectionState === "reconnecting" ? "Reconnecting…" : "Live updates unavailable"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className={`hover:text-text-primary ${
              showHistory ? "text-text-primary" : "text-text-tertiary"
            }`}
            title="Past conversations"
            aria-pressed={showHistory}
          >
            <History size={16} />
          </button>
          <button
            type="button"
            onClick={startNewChat}
            className="text-text-tertiary hover:text-text-primary"
            title="New conversation"
          >
            <MessageSquarePlus size={16} />
          </button>
          <button
            type="button"
            onClick={close}
            className="text-text-tertiary hover:text-text-primary"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      {currentThread ? (
        <ChatThreadView thread={currentThread} />
      ) : showHistory ? (
        <div className="flex-1 overflow-y-auto">
          <ThreadList />
        </div>
      ) : (
        <ChatComposerView onSend={handleNewThreadMessage} disabled={isSending} />
      )}
    </div>
  );
}
